import type { CartState } from "@ai-guidance/shared";
import { commerceText, type CommerceCopy } from "./text";
import type { AdapterResult, CommerceAdapter, CommerceConfig, MutationContext, SearchResults } from "./types";

/**
 * Adaptorul Shopify: Ajax Cart API, cele trei rute publice ale oricărui magazin
 * (shopify.dev → Cart API):
 *   POST /cart/add.js      { items: [{ id, quantity }] }
 *   POST /cart/change.js   { id, quantity }   (0 = scoate linia)
 *   GET  /cart.js          → coșul întreg
 *
 * De ce API și nu click-uri în DOM: e contract public, nu se schimbă la primul
 * redesign de temă, și e AJAX - deci pagina nu se reîncarcă, deci conversația
 * vocală nu se întrerupe (docs/plan-commerce-actions.md §1).
 *
 * Nimic din fișierul ăsta nu aruncă: fiecare drum de eșec (rețea, 422 „sold
 * out", JSON stricat, temă care servește HTML pe /cart.js) iese ca
 * `{ ok: false, message }`, respectiv `null` pentru `getCart`.
 */

/**
 * `id`-ul pentru /cart/add.js: acolo `items[].id` e numeric, iar catalogul îl
 * poartă ca string - deci îl traducem la trimitere. Orice formă care nu e din
 * cifre (o cheie de linie, un id neașteptat) trece neatinsă: platforma decide,
 * nu noi.
 */
function wireId(variantId: string): number | string {
  return /^\d+$/.test(variantId) ? Number(variantId) : variantId;
}

/**
 * `id`-ul pentru /cart/change.js, unde regula e ALTA decât la adăugare: câmpul e
 * validat contra unui regex, iar un regex se aplică pe String - deci un număr JSON
 * e refuzat cu chiar clasa lui în mesaj:
 *
 *   expected Integer to be a (?-mix:\A\d+\z)|(?-mix:\A\d+:[a-f0-9]{32}\z): id
 *
 * Exact asta a văzut vizitatorul pe demo3 (9 aug 2026) când a cerut scoaterea
 * unei linii din coș: adăugarea mergea (acolo numărul e acceptat), scoaterea nu.
 * Deci trimitem STRING - forma pe care o acceptă ambele variante de `id` admise
 * de rută: variant id („794864053") și cheie de linie („794864053:abc…32hex").
 */
function changeId(variantId: string): string {
  return String(variantId);
}

/** Cât păstrăm dintr-un mesaj de eroare al platformei (schema îl plafonează oricum la 200). */
const MAX_ERROR_CHARS = 160;

interface ShopifyCartItem {
  variant_id?: unknown;
  id?: unknown;
  title?: unknown;
  quantity?: unknown;
  line_price?: unknown;
}

interface ShopifyCart {
  item_count?: unknown;
  total_price?: unknown;
  currency?: unknown;
  items?: unknown;
}

/**
 * Sumele Ajax API vin în subunități (bani/cenți), iar moneda vine în același
 * răspuns - deci n-o presupunem niciodată. `ro-RO` e locale-ul de afișare al
 * POC-ului (magazinul demo e în română); moneda rămâne a magazinului, oricare
 * ar fi ea. Un `Intl` care nu recunoaște moneda (date stricate) nu are voie să
 * arunce în mijlocul unei capturi de snapshot - de-aia fallback-ul.
 */
export function formatMoney(cents: number, currency: string): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat("ro-RO", { style: "currency", currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`.trim();
  }
}

function asNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function asText(value: unknown, max: number): string {
  return typeof value === "string" ? value.slice(0, max) : "";
}

/** Răspunsul /cart.js → `CartState`, tăiat la plafoanele schemei partajate. */
export function toCartState(raw: unknown): CartState | null {
  if (!raw || typeof raw !== "object") return null;
  const cart = raw as ShopifyCart;
  // Un magazin real întoarce mereu `item_count`; o temă care servește HTML pe
  // /cart.js (sau un sit care nu e Shopify) nu - și atunci nu inventăm un coș gol.
  if (typeof cart.item_count !== "number") return null;
  const currency = asText(cart.currency, 10) || "RON";
  const rawItems = Array.isArray(cart.items) ? (cart.items as ShopifyCartItem[]) : [];
  const items = rawItems.slice(0, 100).map((item) => {
    const linePrice = typeof item.line_price === "number" ? formatMoney(item.line_price, currency) : undefined;
    return {
      variantId: String(item.variant_id ?? item.id ?? "").slice(0, 64),
      title: asText(item.title, 300),
      quantity: Math.min(999, Math.max(0, Math.round(asNumber(item.quantity)))),
      ...(linePrice ? { linePrice: linePrice.slice(0, 50) } : {}),
    };
  });
  return {
    items,
    itemCount: Math.min(9999, Math.max(0, Math.round(cart.item_count))),
    totalPrice: formatMoney(asNumber(cart.total_price), currency).slice(0, 50),
  };
}

/**
 * Eroarea platformei e uneori un mesaj pentru OM („Nu mai avem decât 2 bucăți"),
 * și uneori un mesaj pentru DEZVOLTATOR - validarea unui parametru, cu regexul
 * ei cu tot:
 *
 *   expected Integer to be a (?-mix:\A\d+\z)|(?-mix:\A\d+:[a-f0-9]{32}\z): id
 *
 * Pe al doilea l-a citit un vizitator real într-un toast, pe demo3 (9 aug 2026).
 * Un asemenea text nu spune nimic nimănui în afară de noi: nu e în limba
 * magazinului, nu se poate acționa pe el, și arată ca un sit stricat. Deci îl
 * recunoaștem și cădem pe textul nostru, iar cauza rămâne în consolă.
 *
 * Recunoașterea e pe FORMĂ, nu pe mesajul anume: sintaxă de regex Ruby, secvențe
 * de escape, sau tiparul „expected X to be a Y" al validatorului lor. Un mesaj
 * de stoc n-are cum să arate așa.
 */
const TECHNICAL_ERROR_PATTERNS = [/\(\?-mix:/, /\\[Azdw]/, /expected\s+\w+\s+to\s+be\s+an?\b/i];

function looksTechnical(text: string): boolean {
  return TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

/** Mesajul scurt al unui eșec, preferând descrierea platformei („You can't add more…"). */
function errorMessage(body: unknown, copy: CommerceCopy): string {
  if (body && typeof body === "object") {
    const err = body as { description?: unknown; message?: unknown };
    const text = typeof err.description === "string" && err.description.trim()
      ? err.description
      : typeof err.message === "string"
        ? err.message
        : "";
    const trimmed = text.trim();
    if (trimmed && looksTechnical(trimmed)) {
      // În consolă, nu în toast: e diagnosticul nostru, nu al vizitatorului.
      console.warn("[ai-guidance] cart API rejected the request:", trimmed.slice(0, MAX_ERROR_CHARS));
      return copy.cartFailed;
    }
    if (trimmed) return trimmed.slice(0, MAX_ERROR_CHARS);
  }
  return copy.cartFailed;
}

/**
 * Parametrii prin care Ajax Cart API re-randează secțiunile temei în ACELAȘI
 * răspuns cu mutația (shopify.dev → Cart API, „Sections"). Fără ei am face un
 * al doilea drum doar ca să aflăm HTML-ul nou al sertarului.
 *
 * `sections_url` spune față de CE pagină se randează: sertarul e montat din
 * layout, deci contextul corect e pagina curentă - exact ce trimite și tema când
 * își cere singură secțiunile (`window.location.pathname` în
 * `component-cart-items.js`).
 */
function sectionsBody(ctx?: MutationContext): Record<string, unknown> {
  const ids = ctx?.sections ?? [];
  if (ids.length === 0) return {};
  return {
    sections: ids.join(","),
    sections_url: ctx?.sectionsUrl ?? location.pathname,
  };
}

/** `response.sections` = { sectionId: html }; orice altă formă → „n-am secțiuni". */
function readSections(body: unknown): Record<string, string> | undefined {
  if (!body || typeof body !== "object") return undefined;
  const raw = (body as { sections?: unknown }).sections;
  if (!raw || typeof raw !== "object") return undefined;
  const out: Record<string, string> = {};
  for (const [id, html] of Object.entries(raw as Record<string, unknown>)) {
    // Shopify întoarce `null` pentru o secțiune inexistentă; morph-ul temei ar
    // arunca pe ea, deci n-o trecem mai departe.
    if (typeof html === "string" && html !== "") out[id] = html;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Numărătoarea rezultatelor de pe pagina temei - EURISTICĂ, și e cinstit spus.
 *
 * Nu există contract de platformă pentru „câte produse s-au randat": grila e
 * markup de temă, diferit de la temă la temă. Deci numărăm ce e comun tuturor
 * temelor: LINKURI către `/products/<handle>`, în zona principală de conținut,
 * deduplicate pe handle (un card are de obicei 2-3 linkuri - imagine, titlu,
 * buton - toate spre același produs).
 *
 * Regula de aur: un `0` MINCINOS e mai rău decât lipsa semnalului, fiindcă
 * declanșează recuperarea pe empty-results peste o pagină plină. De-aceea zero
 * linkuri se raportează ca `0` doar când tema a randat vizibil o zonă de
 * rezultate (container de grilă) sau un marker de „gol"; în orice alt caz -
 * temă necunoscută, grilă randată din JS care n-a sosit încă, carduri fără
 * linkuri - întoarcem `undefined`, adică „nu știu", nu „nu e nimic".
 */

/** Prefixele de cale care înseamnă „pagină de rezultate a temei". */
function searchPagePath(pathname: string): boolean {
  // Magazinele multi-limbă prefixează calea (`/en/search`, `/fr-ca/collections/x`),
  // deci potrivim pe SEGMENTE, nu pe începutul șirului.
  const segments = pathname.split("/").filter(Boolean);
  const idx = segments.indexOf("search");
  if (idx === 0 || idx === 1) return true;
  const col = segments.indexOf("collections");
  return col === 0 || col === 1;
}

/** Zona principală de conținut - restrânge numărătoarea, ca să nu prindem headerul și footerul. */
const MAIN_SELECTORS = "main, #MainContent, [role='main'], #main, .main-content";

/**
 * Containerul de grilă al temei. Prezența lui e dovada că tema a randat zona de
 * rezultate - deci un zero din interiorul lui e un zero real.
 */
const GRID_SELECTORS = [
  "#product-grid",
  "[id*='product-grid']",
  "[class*='product-grid']",
  "[class*='product-list']",
  "[class*='products-grid']",
  "[class*='collection__products']",
  "[class*='search-results']",
  "[class*='results-list']",
].join(",");

/** Starea „nimic găsit" randată de temă (Dawn: `template-search--empty`, `collection--empty`). */
const EMPTY_SELECTORS = "[class*='empty'],[class*='no-results'],[class*='noresults']";

/** Plafon sănătos: peste atât cifra exactă nu mai spune nimic în plus agentului. */
const SEARCH_RESULTS_MAX_COUNT = 200;
const SEARCH_QUERY_MAX_CHARS = 200;

/** Handle-ul de produs dintr-un href, sau `null` dacă linkul nu duce la un produs. */
function productHandle(href: string): string | null {
  // `href` e deja rezolvat absolut de DOM; ne interesează doar calea, fără
  // `?variant=` - două linkuri spre variante ale aceluiași produs sunt UN card.
  const match = /\/products\/([^/?#]+)/.exec(href);
  return match ? decodeURIComponent(match[1]).toLowerCase() : null;
}

function countProducts(scope: ParentNode): number {
  const handles = new Set<string>();
  for (const link of Array.from(scope.querySelectorAll("a[href*='/products/']"))) {
    const handle = productHandle((link as HTMLAnchorElement).getAttribute("href") ?? "");
    if (handle) handles.add(handle);
  }
  return handles.size;
}

function readSearchResults(): SearchResults | undefined {
  if (typeof document === "undefined" || typeof location === "undefined") return undefined;
  if (!searchPagePath(location.pathname)) return undefined;

  const raw = new URLSearchParams(location.search).get("q");
  const query = raw ? raw.trim().slice(0, SEARCH_QUERY_MAX_CHARS) : "";
  const withQuery = (count: number): SearchResults => ({
    count: Math.min(count, SEARCH_RESULTS_MAX_COUNT),
    ...(query ? { query } : {}),
  });

  // Fără `main` cădem pe `body`: o temă care nu marchează zona principală tot
  // trebuie numărată. Riscul asumat e în SUS (o secțiune „recomandate" din
  // footer intră în total), niciodată în JOS - și doar cifra exactă suferă,
  // niciodată verdictul „gol / nu e gol", care e ce declanșează recuperarea.
  const main = document.querySelector(MAIN_SELECTORS);
  const scope = main ?? document.body;
  if (!scope) return undefined;

  const count = countProducts(scope);
  if (count > 0) return withQuery(count);

  // Zero linkuri în zona principală: abdicăm, DACĂ nu vedem cu ochii noștri că
  // tema chiar a randat o zonă de rezultate (grilă) sau o stare de „gol".
  const rendered = scope.querySelector(GRID_SELECTORS) ?? scope.querySelector(EMPTY_SELECTORS);
  return rendered ? withQuery(0) : undefined;
}

export function createShopifyAdapter(config: CommerceConfig, copy: CommerceCopy = commerceText()): CommerceAdapter {
  /**
   * `credentials: "same-origin"` e explicit fiindcă TOT coșul stă în cookie-ul
   * `cart` al magazinului: fără el, fiecare cerere ar deschide un coș nou și
   * vizitatorul n-ar vedea niciodată ce i-am adăugat.
   */
  async function post(path: string, body: Record<string, unknown>, ctx?: MutationContext): Promise<AdapterResult> {
    try {
      const res = await fetch(path, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ ...body, ...sectionsBody(ctx) }),
      });
      if (!res.ok) {
        // 422 „Cart Error" e cazul normal, nu excepțional: stoc epuizat, mai
        // mult decât e disponibil, variantă dispărută. Textul platformei e deja
        // în limba magazinului și e mai exact decât orice am scrie noi.
        const payload = await res.json().catch(() => null);
        return { ok: false, message: errorMessage(payload, copy) };
      }
      // Corpul îl citim DOAR pentru secțiuni, și nimic din el nu are voie să
      // strice un succes: o temă care întoarce altceva decât JSON înseamnă „fără
      // sincronizare într-un singur drum", nu „adăugarea a eșuat".
      const sections = ctx?.sections?.length ? readSections(await res.json().catch(() => null)) : undefined;
      return sections ? { ok: true, sections } : { ok: true };
    } catch {
      return { ok: false, message: copy.cartFailed };
    }
  }

  return {
    platform: "shopify",
    addToCart(variantId, quantity, ctx) {
      return post("/cart/add.js", { items: [{ id: wireId(variantId), quantity }] }, ctx);
    },
    setQuantity(variantId, quantity, ctx) {
      // `id` acceptă și variant id, și cheia de linie; noi avem mereu variant
      // id-ul din catalog. `quantity: 0` scoate linia - nu există rută separată.
      // String, nu număr - vezi `changeId`: aici e singura diferență față de
      // adăugare, și e chiar cea care rupea scoaterea din coș.
      return post("/cart/change.js", { id: changeId(variantId), quantity }, ctx);
    },
    async getCart() {
      try {
        const res = await fetch("/cart.js", {
          credentials: "same-origin",
          headers: { accept: "application/json" },
        });
        if (!res.ok) return null;
        return toCartState(await res.json());
      } catch {
        return null;
      }
    },
    cartPath() {
      return config.cartPath;
    },
    /**
     * Căutarea Shopify e condusă prin URL - `/search?q=...` e rută de platformă,
     * servită de tema magazinului, deci rezultatele apar în grila LUI nativă.
     * Nu simulăm tastarea în bara temei: aia e markup de client, se schimbă la
     * primul redesign, iar formularul ei postează oricum tot aici.
     * `encodeURIComponent` (nu `URLSearchParams`) fiindcă vrem `%20`, nu `+`:
     * ambele merg, dar calea ajunge și în bara de adrese, sub ochii omului.
     */
    searchPath(query, bounds) {
      // Filtrele de preț ale magazinului se pun tot din URL - `filter.v.price.gte`
      // / `filter.v.price.lte`, în monedă întreagă (verificat pe shopify.dev,
      // „Storefront filtering", 9 aug 2026). Valabile pe /search și pe paginile
      // de colecție deopotrivă, deci pagina de rezultate vine DEJA filtrată:
      // nimeni nu trebuie să atingă bara de filtre a temei.
      //
      // Caveat de temă: parametrii sunt citiți de secțiunea de filtrare a temei
      // (Online Store 2.0 + filtrele activate din Search & Discovery). Pe o temă
      // fără filtrare, parametrii sunt pur și simplu ignorați - rezultatele
      // rămân nefiltrate, dar corecte pentru interogare. Deci un magazin fără
      // filtre nu se strică; îi lipsește doar strâmtarea.
      const params = [`q=${encodeURIComponent(query.trim())}`];
      if (typeof bounds?.minPrice === "number") params.push(`filter.v.price.gte=${bounds.minPrice}`);
      if (typeof bounds?.maxPrice === "number") params.push(`filter.v.price.lte=${bounds.maxPrice}`);
      return `/search?${params.join("&")}`;
    },
    searchResults() {
      return readSearchResults();
    },
  };
}
